<template>
  <div class="setmeal-series-tree">
    <div class="tree-header" v-if="showHeader">
      <h3>套餐系列结构</h3>
      <div class="tree-actions">
        <el-button type="primary" size="mini" @click="handleRefresh">
          <i class="fa fa-refresh"></i> 刷新
        </el-button>
        <el-button type="success" size="mini" @click="handleExpandAll">
          <i class="fa fa-expand"></i> 展开全部
        </el-button>
        <el-button type="warning" size="mini" @click="handleCollapseAll">
          <i class="fa fa-compress"></i> 收起全部
        </el-button>
      </div>
    </div>
    
    <div class="tree-content" v-loading="loading">
      <el-tree
        ref="seriesTree"
        :data="treeData"
        :show-checkbox="showCheckbox"
        :check-strictly="checkStrictly"
        :node-key="nodeKey"
        :props="treeProps"
        :expand-on-click-node="expandOnClickNode"
        :default-expand-all="defaultExpandAll"
        @node-click="handleNodeClick"
        @check-change="handleCheckChange"
        @node-expand="handleNodeExpand"
        @node-collapse="handleNodeCollapse">
        
        <span class="custom-tree-node" slot-scope="{ node, data }">
          <span class="node-content">
            <!-- 图标 -->
            <i :class="getNodeIcon(data)" :style="{ color: getNodeIconColor(data) }"></i>
            
            <!-- 名称 -->
            <span class="node-name">{{ node.label }}</span>
            
            <!-- 状态标签 -->
            <el-tag 
              v-if="showStatus"
              :type="data.status === 1 ? 'success' : 'danger'" 
              size="mini"
              class="node-status">
              {{ data.status === 1 ? '启用' : '禁用' }}
            </el-tag>
            
            <!-- 层级标签 -->
            <el-tag 
              v-if="showLevel"
              type="info" 
              size="mini"
              class="node-level">
              {{ data.level }}级
            </el-tag>
            
            <!-- 套餐数量 -->
            <span 
              v-if="showMealCount"
              class="node-meal-count"
              :style="{ color: data.mealCount > 0 ? '#409EFF' : '#909399' }">
              ({{ data.mealCount || 0 }}个套餐)
            </span>
            
            <!-- 描述 -->
            <span 
              v-if="showDescription && data.description"
              class="node-description"
              :title="data.description">
              - {{ data.description }}
            </span>
          </span>
          
          <!-- 操作按钮 -->
          <span v-if="showActions" class="node-actions">
            <el-button 
              v-if="allowEdit"
              type="text" 
              size="mini" 
              @click.stop="handleEdit(data)">
              编辑
            </el-button>
            <el-button 
              v-if="allowAddChild && data.level < maxLevel"
              type="text" 
              size="mini" 
              @click.stop="handleAddChild(data)">
              添加子系列
            </el-button>
            <el-button 
              v-if="allowDelete && canDelete(data)"
              type="text" 
              size="mini" 
              class="danger-button"
              @click.stop="handleDelete(data)">
              删除
            </el-button>
          </span>
        </span>
      </el-tree>
      
      <!-- 空状态 -->
      <div v-if="!loading && treeData.length === 0" class="empty-state">
        <i class="fa fa-folder-o empty-icon"></i>
        <p class="empty-text">暂无系列数据</p>
        <el-button v-if="allowAdd" type="primary" @click="handleAdd">
          创建第一个系列
        </el-button>
      </div>
    </div>
    
    <!-- 统计信息 -->
    <div v-if="showStatistics && statistics" class="tree-statistics">
      <div class="statistics-item">
        <span class="statistics-label">总系列：</span>
        <span class="statistics-value">{{ statistics.totalSeries }}</span>
      </div>
      <div class="statistics-item">
        <span class="statistics-label">启用系列：</span>
        <span class="statistics-value success">{{ statistics.activeSeries }}</span>
      </div>
      <div class="statistics-item">
        <span class="statistics-label">总套餐：</span>
        <span class="statistics-value primary">{{ statistics.totalMeals }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getSeriesTree, getSeriesStatistics } from '@/api/setMealManagment/setMealSeries.js'

export default {
  name: 'SetMealSeriesTree',
  props: {
    // 是否显示头部
    showHeader: {
      type: Boolean,
      default: true
    },
    // 是否显示复选框
    showCheckbox: {
      type: Boolean,
      default: false
    },
    // 复选框是否严格模式（父子不关联）
    checkStrictly: {
      type: Boolean,
      default: false
    },
    // 节点key
    nodeKey: {
      type: String,
      default: 'id'
    },
    // 是否点击节点展开/收起
    expandOnClickNode: {
      type: Boolean,
      default: false
    },
    // 默认是否展开全部
    defaultExpandAll: {
      type: Boolean,
      default: true
    },
    // 是否显示状态
    showStatus: {
      type: Boolean,
      default: true
    },
    // 是否显示层级
    showLevel: {
      type: Boolean,
      default: true
    },
    // 是否显示套餐数量
    showMealCount: {
      type: Boolean,
      default: true
    },
    // 是否显示描述
    showDescription: {
      type: Boolean,
      default: false
    },
    // 是否显示操作按钮
    showActions: {
      type: Boolean,
      default: true
    },
    // 是否显示统计信息
    showStatistics: {
      type: Boolean,
      default: true
    },
    // 是否允许编辑
    allowEdit: {
      type: Boolean,
      default: true
    },
    // 是否允许添加子系列
    allowAddChild: {
      type: Boolean,
      default: true
    },
    // 是否允许删除
    allowDelete: {
      type: Boolean,
      default: true
    },
    // 是否允许新增根系列
    allowAdd: {
      type: Boolean,
      default: true
    },
    // 最大层级
    maxLevel: {
      type: Number,
      default: 3
    },
    // 只显示启用的系列
    showOnlyActive: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      loading: false,
      treeData: [],
      statistics: null,
      treeProps: {
        label: 'name',
        children: 'children'
      }
    }
  },
  
  mounted() {
    this.loadData()
    if (this.showStatistics) {
      this.loadStatistics()
    }
  },
  
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const status = this.showOnlyActive ? 1 : null
        const response = await getSeriesTree(status)
        if (response.data && response.data.code === 0) {
          this.treeData = response.data.data || []
        }
      } catch (error) {
        this.$message.error('加载系列数据失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    
    // 加载统计信息
    async loadStatistics() {
      try {
        const response = await getSeriesStatistics()
        if (response.data && response.data.code === 0) {
          this.statistics = response.data.data
        }
      } catch (error) {
        console.error('加载统计信息失败:', error)
      }
    },
    
    // 刷新数据
    handleRefresh() {
      this.loadData()
      if (this.showStatistics) {
        this.loadStatistics()
      }
      this.$emit('refresh')
    },
    
    // 展开全部
    handleExpandAll() {
      this.$refs.seriesTree.filter()
      this.expandAll(this.treeData, true)
    },
    
    // 收起全部
    handleCollapseAll() {
      this.expandAll(this.treeData, false)
    },
    
    // 递归展开/收起
    expandAll(data, isExpand) {
      data.forEach(item => {
        this.$refs.seriesTree.store.nodesMap[item.id].expanded = isExpand
        if (item.children && item.children.length > 0) {
          this.expandAll(item.children, isExpand)
        }
      })
    },
    
    // 获取节点图标
    getNodeIcon(data) {
      if (data.level === 1) {
        return 'fa fa-folder'
      } else if (data.level === 2) {
        return 'fa fa-folder-o'
      } else {
        return 'fa fa-file-o'
      }
    },
    
    // 获取节点图标颜色
    getNodeIconColor(data) {
      if (data.status === 2) {
        return '#909399' // 禁用状态为灰色
      }
      
      if (data.level === 1) {
        return '#409EFF' // 一级系列为蓝色
      } else if (data.level === 2) {
        return '#67C23A' // 二级系列为绿色
      } else {
        return '#E6A23C' // 三级及以上为橙色
      }
    },
    
    // 是否可以删除
    canDelete(data) {
      // 有子系列或有套餐的不能删除
      return (!data.children || data.children.length === 0) && 
             (!data.mealCount || data.mealCount === 0)
    },
    
    // 节点点击事件
    handleNodeClick(data, node) {
      this.$emit('node-click', data, node)
    },
    
    // 复选框改变事件
    handleCheckChange(data, checked, indeterminate) {
      this.$emit('check-change', data, checked, indeterminate)
    },
    
    // 节点展开事件
    handleNodeExpand(data, node) {
      this.$emit('node-expand', data, node)
    },
    
    // 节点收起事件
    handleNodeCollapse(data, node) {
      this.$emit('node-collapse', data, node)
    },
    
    // 编辑系列
    handleEdit(data) {
      this.$emit('edit', data)
    },
    
    // 添加子系列
    handleAddChild(data) {
      this.$emit('add-child', data)
    },
    
    // 删除系列
    handleDelete(data) {
      this.$confirm(`确认删除系列"${data.name}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$emit('delete', data)
      })
    },
    
    // 新增根系列
    handleAdd() {
      this.$emit('add')
    },
    
    // 获取选中的节点
    getCheckedNodes(leafOnly = false, includeHalfChecked = false) {
      return this.$refs.seriesTree.getCheckedNodes(leafOnly, includeHalfChecked)
    },
    
    // 获取选中的节点key
    getCheckedKeys(leafOnly = false) {
      return this.$refs.seriesTree.getCheckedKeys(leafOnly)
    },
    
    // 设置选中的节点
    setCheckedKeys(keys) {
      this.$refs.seriesTree.setCheckedKeys(keys)
    },
    
    // 设置选中的节点
    setCheckedNodes(nodes) {
      this.$refs.seriesTree.setCheckedNodes(nodes)
    },
    
    // 过滤节点
    filterNode(value, data) {
      if (!value) return true
      return data.name.indexOf(value) !== -1
    }
  },
  
  watch: {
    showOnlyActive() {
      this.loadData()
    }
  }
}
</script>

<style scoped>
.setmeal-series-tree {
  background: #fff;
  border-radius: 4px;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebeef5;
}

.tree-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.tree-actions {
  display: flex;
  gap: 8px;
}

.tree-content {
  padding: 16px;
  min-height: 200px;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  min-height: 32px;
}

.node-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.node-content i {
  margin-right: 8px;
  font-size: 16px;
}

.node-name {
  font-weight: 500;
  color: #303133;
  margin-right: 8px;
}

.node-status {
  margin-right: 8px;
}

.node-level {
  margin-right: 8px;
}

.node-meal-count {
  font-size: 12px;
  margin-right: 8px;
}

.node-description {
  font-size: 12px;
  color: #909399;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s;
}

.custom-tree-node:hover .node-actions {
  opacity: 1;
}

.danger-button {
  color: #f56c6c !important;
}

.danger-button:hover {
  color: #f78989 !important;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.empty-text {
  font-size: 14px;
  margin-bottom: 16px;
}

.tree-statistics {
  display: flex;
  justify-content: space-around;
  padding: 16px;
  border-top: 1px solid #ebeef5;
  background-color: #fafafa;
}

.statistics-item {
  text-align: center;
}

.statistics-label {
  font-size: 12px;
  color: #909399;
}

.statistics-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-left: 4px;
}

.statistics-value.success {
  color: #67c23a;
}

.statistics-value.primary {
  color: #409eff;
}

/* 响应式 */
@media (max-width: 768px) {
  .tree-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .tree-statistics {
    flex-direction: column;
    gap: 12px;
  }
  
  .custom-tree-node {
    flex-direction: column;
    align-items: flex-start;
    min-height: auto;
    padding: 8px 0;
  }
  
  .node-actions {
    margin-top: 8px;
    opacity: 1;
  }
}
</style>
